import { memo, useState, useEffect, useCallback } from "react";
import { useI18n } from "@/i18n";
import { warehouseClient } from "@/infrastructure/http/warehouseClient";
import { colors, typography } from "@/design-system/tokens";
import type { ApiEnvelope } from "@/domain/types";

interface WarehouseStats {
  sectionUsage: { totalShelves: number; emptyShelves: number; fullShelves: number; newlyAdded: number; usagePercentage: number };
  inventoryOverview: { receiptsCount: number; receiptsChangePercent: number; dispatchesCount: number; dispatchesChangePercent: number; cancelledCount: number; cancelledChangePercent: number };
}

async function fetchWarehouseStats(warehouseId: string, isleId?: string): Promise<WarehouseStats> {
  const query = isleId ? `?isleId=${isleId}` : "";
  const res = await warehouseClient.get<ApiEnvelope<WarehouseStats>>(`/warehouse/${warehouseId}/stats${query}`);
  return res.data.data;
}

export const UsagePanel = memo(function UsagePanel({ warehouseId, isleId }: { warehouseId?: string; isleId?: string }) {
  const { t } = useI18n();
  const [stats, setStats] = useState<WarehouseStats | null>(null);
  const [loading, setLoading] = useState(false);

  useEffect(() => {
    if (!warehouseId) return;
    setLoading(true);
    fetchWarehouseStats(warehouseId, isleId)
      .then(setStats)
      .catch(() => setStats(null))
      .finally(() => setLoading(false));
  }, [warehouseId, isleId]);

  if (!warehouseId || loading || !stats) {
    return (
      <div style={{ display: "flex", flexDirection: "column", gap: "20px", minWidth: "280px", maxWidth: "320px", flex: "1 1 280px" }}>
        <div style={{ background: "#f8f8f8", borderRadius: "14px", border: "1px solid #efefef", padding: "22px", minHeight: "180px", display: "flex", alignItems: "center", justifyContent: "center" }}>
          {loading && <span style={{ width: "20px", height: "20px", border: `2px solid ${colors.primary}`, borderTopColor: "transparent", borderRadius: "50%", display: "inline-block", animation: "spin 0.7s linear infinite" }} />}
        </div>
      </div>
    );
  }

  const { sectionUsage, inventoryOverview } = stats;

  return (
    <div style={{ display: "flex", flexDirection: "column", gap: "20px", minWidth: "280px", maxWidth: "320px", flex: "1 1 280px" }}>
      <UsageCard usage={sectionUsage} />
      <InventoryCard overview={inventoryOverview} />
    </div>
  );
});

const UsageCard = memo(function UsageCard({ usage }: { usage: WarehouseStats["sectionUsage"] }) {
  const { t } = useI18n();
  return (
    <div style={{ background: "#f8f8f8", borderRadius: "14px", border: "1px solid #efefef", padding: "22px", display: "flex", flexDirection: "column", gap: "20px" }}>
      <h3 style={{ fontFamily: typography.fontFamily, fontSize: typography.size.base, fontWeight: typography.weight.bold, color: "#111", margin: 0 }}>
        {t.isle.sectionOverview}
      </h3>
      <div style={{ display: "flex", alignItems: "center", gap: "20px" }}>
        <DonutChart pct={usage.usagePercentage} />
        <div style={{ display: "grid", gridTemplateColumns: "1fr 1fr", gap: "12px 20px" }}>
          <Stat value={usage.totalShelves} label={t.isle.cellsLabel} />
          <Stat value={usage.emptyShelves} label={t.location.activeLabel} />
          <Stat value={usage.fullShelves} label={t.location.inactiveLabel} />
          <Stat value={usage.newlyAdded} label="+" />
        </div>
      </div>
    </div>
  );
});

const InventoryCard = memo(function InventoryCard({ overview }: { overview: WarehouseStats["inventoryOverview"] }) {
  const { t } = useI18n();
  return (
    <div style={{ background: "#f8f8f8", borderRadius: "14px", border: "1px solid #efefef", padding: "22px", display: "flex", flexDirection: "column", gap: "16px" }}>
      <h3 style={{ fontFamily: typography.fontFamily, fontSize: typography.size.base, fontWeight: typography.weight.bold, color: "#111", margin: 0 }}>
        {t.platform.inventory}
      </h3>
      <div style={{ display: "grid", gridTemplateColumns: "1fr 1fr", gap: "12px" }}>
        <MetricCard icon={<ReceivedIcon />} value={overview.receiptsCount} label={t.platform.receipts} delta={overview.receiptsChangePercent} />
        <MetricCard icon={<ShippedIcon />} value={overview.dispatchesCount} label={t.platform.dispatches} delta={overview.dispatchesChangePercent} />
        <MetricCard icon={<CanceledIcon />} value={overview.cancelledCount} label={t.receipts.statusCancelled} delta={overview.cancelledChangePercent} />
      </div>
    </div>
  );
});

const DonutChart = memo(function DonutChart({ pct }: { pct: number }) {
  const r = 38;
  const circ = 2 * Math.PI * r;
  const filled = (pct / 100) * circ;
  return (
    <div style={{ position: "relative", flexShrink: 0 }}>
      <svg width="96" height="96" viewBox="0 0 96 96">
        <circle cx="48" cy="48" r={r} fill="none" stroke="#ebebeb" strokeWidth="9" />
        <circle cx="48" cy="48" r={r} fill="none" stroke={colors.primary} strokeWidth="9" strokeDasharray={`${filled} ${circ - filled}`} strokeDashoffset={circ / 4} strokeLinecap="round" />
      </svg>
      <div style={{ position: "absolute", inset: 0, display: "flex", flexDirection: "column", alignItems: "center", justifyContent: "center", gap: "2px" }}>
        <span style={{ fontFamily: typography.fontFamily, fontSize: "16px", fontWeight: typography.weight.bold, color: "#111", lineHeight: 1 }}>{pct}%</span>
        <span style={{ fontFamily: typography.fontFamily, fontSize: "9px", color: "#aaa", lineHeight: 1 }}>used</span>
      </div>
    </div>
  );
});

const Stat = memo(function Stat({ value, label }: { value: number; label: string }) {
  return (
    <div style={{ display: "flex", flexDirection: "column", gap: "2px" }}>
      <span style={{ fontFamily: typography.fontFamily, fontSize: typography.size.base, fontWeight: typography.weight.bold, color: "#111", lineHeight: 1 }}>{value}</span>
      <span style={{ fontFamily: typography.fontFamily, fontSize: "10px", color: "#aaa", lineHeight: 1.3 }}>{label}</span>
    </div>
  );
});

const MetricCard = memo(function MetricCard({ icon, value, label, delta }: { icon: React.ReactNode; value: number; label: string; delta: number }) {
  const positive = delta >= 0;
  const deltaColor = positive ? "#22c55e" : "#ef4444";
  return (
    <div style={{ background: "#fff", borderRadius: "12px", border: "1px solid #efefef", padding: "14px", display: "flex", flexDirection: "column", gap: "8px" }}>
      <div style={{ display: "flex", alignItems: "center", justifyContent: "space-between" }}>
        <span style={{ color: "#ccc" }}>{icon}</span>
        <span style={{ fontFamily: typography.fontFamily, fontSize: "11px", fontWeight: typography.weight.bold, color: deltaColor }}>
          {Math.abs(delta)}% {positive ? "↑" : "↓"}
        </span>
      </div>
      <span style={{ fontFamily: typography.fontFamily, fontSize: "1.25rem", fontWeight: typography.weight.bold, color: "#111", letterSpacing: "-0.03em", lineHeight: 1 }}>{value.toLocaleString()}</span>
      <span style={{ fontFamily: typography.fontFamily, fontSize: "11px", color: "#aaa", lineHeight: 1.3 }}>{label}</span>
    </div>
  );
});

function ReceivedIcon() { return <svg width="18" height="18" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.8"><path d="M14 2H6a2 2 0 0 0-2 2v16a2 2 0 0 0 2 2h12a2 2 0 0 0 2-2V8z" /><polyline points="14 2 14 8 20 8" /><line x1="16" y1="13" x2="8" y2="13" /><line x1="16" y1="17" x2="8" y2="17" /></svg>; }
function ShippedIcon() { return <svg width="18" height="18" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.8"><path d="M5 12h14" /><polyline points="12 5 19 12 12 19" /></svg>; }
function CanceledIcon() { return <svg width="18" height="18" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.8"><circle cx="12" cy="12" r="10" /><line x1="15" y1="9" x2="9" y2="15" /><line x1="9" y1="9" x2="15" y2="15" /></svg>; }
